import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { KeyRound, Link2, Unlink } from "lucide-react";
import { errorMessage } from "@atlas/shared";
import type { SignInMethod, SignInMethodsResponse, SocialSignInInput } from "@atlas/shared";
import {
  SIGN_IN_METHODS_QUERY_KEY,
  fetchSignInMethods,
  linkSignInMethod,
  unlinkSignInMethod,
} from "../api/account";
import { AtlasPanel } from "./AtlasPanel";
import { Button } from "./Button";
import { AppleMark, SocialSignInButtons, hasSocialProvider } from "./SocialSignInButtons";

// Account page panel (ATL-130): every way into this account, with Link for
// the social providers that are not yet attached and Unlink for the ones
// that are. The server refuses an unlink that would leave no way to sign
// in; the button is disabled ahead of that so the refusal is never a surprise.

const LABELS: Record<SignInMethod["provider"], string> = {
  email: "Email and password",
  apple: "Apple",
  google: "Google",
  strava: "Strava",
};

function GoogleMark({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

function MethodIcon({ provider }: { provider: SignInMethod["provider"] }) {
  if (provider === "apple") return <AppleMark size={16} />;
  if (provider === "google") return <GoogleMark size={16} />;
  // Strava has no small mark in our asset set (only the official buttons and
  // "Powered by" lockups, never redrawn), so a neutral link icon stands in.
  if (provider === "strava") return <Link2 size={16} />;
  return <KeyRound size={16} />;
}

export function SignInMethodsPanel() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: SIGN_IN_METHODS_QUERY_KEY, queryFn: fetchSignInMethods });
  const [error, setError] = React.useState<string | null>(null);

  const apply = (data: SignInMethodsResponse) => {
    queryClient.setQueryData(SIGN_IN_METHODS_QUERY_KEY, data);
    void queryClient.invalidateQueries({ queryKey: ["me"] });
  };

  const linkMutation = useMutation({
    mutationFn: (input: SocialSignInInput) => linkSignInMethod(input),
    onSuccess: apply,
    onError: (err) => setError(errorMessage(err, "Couldn't link that account.")),
  });
  const unlinkMutation = useMutation({
    mutationFn: (provider: "apple" | "google") => unlinkSignInMethod(provider),
    onSuccess: apply,
    onError: (err) => setError(errorMessage(err, "Couldn't unlink that account.")),
  });

  const methods = query.data?.methods ?? [];
  const canUnlink = query.data?.canUnlink ?? false;
  const linked = new Set(methods.map((m) => m.provider));
  const linkable = (["apple", "google"] as const).filter((p) => hasSocialProvider(p) && !linked.has(p));
  const busy = linkMutation.isPending || unlinkMutation.isPending;

  return (
    <AtlasPanel title="Sign-in methods">
      <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 480 }}>
        <p style={{ fontSize: 13, color: "var(--text-secondary)", margin: 0, lineHeight: 1.5 }}>
          Any of these gets you back into this account. Keep at least one; you can't unlink the
          last.
        </p>

        {query.isPending && (
          <div style={{ fontSize: 13, color: "var(--text-muted)" }}>Loading…</div>
        )}
        {query.isError && (
          <div style={{ fontSize: 13, color: "var(--status-danger)" }}>
            {errorMessage(query.error, "Couldn't load your sign-in methods.")}
          </div>
        )}

        {methods.length > 0 && (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
            {methods.map((m) => (
              <li
                key={m.provider}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  background: "var(--surface-sunken)",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <span
                  style={{
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "var(--radius-sm)",
                    background: m.provider === "apple" ? "#ffffff" : "var(--surface-raised)",
                    color: m.provider === "apple" ? "#000000" : "var(--text-primary)",
                    border: "1px solid var(--border-default)",
                  }}
                >
                  <MethodIcon provider={m.provider} />
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>
                    {LABELS[m.provider]}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-muted)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {m.provider === "email"
                      ? "Password set"
                      : m.provider === "strava"
                        ? "Managed in Connected sources"
                        : m.label ?? "Linked"}
                  </div>
                </div>
                {m.provider === "strava" ? (
                  <Link
                    to="/connected-sources"
                    style={{ fontSize: 13, color: "var(--text-accent)", textDecoration: "none", flexShrink: 0 }}
                  >
                    Manage
                  </Link>
                ) : m.provider === "email" ? (
                  <a href="#security" style={{ fontSize: 13, color: "var(--text-accent)", textDecoration: "none", flexShrink: 0 }}>
                    Change
                  </a>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!canUnlink || busy}
                    title={canUnlink ? undefined : "Set a password or link another method first"}
                    onClick={() => {
                      setError(null);
                      unlinkMutation.mutate(m.provider as "apple" | "google");
                    }}
                  >
                    <Unlink size={13} /> Unlink
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}

        {linkable.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
              <Link2 size={13} /> Add another way in
            </div>
            <SocialSignInButtons
              intent="continue"
              only={linkable}
              width={320}
              pending={busy}
              onCredential={(input) => {
                setError(null);
                linkMutation.mutate(input);
              }}
              onError={setError}
            />
          </div>
        )}

        {error && (
          <div style={{ fontSize: 13, color: "var(--status-danger)" }}>{error}</div>
        )}
      </div>
    </AtlasPanel>
  );
}
